import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, ExternalLink, User, FileText, RotateCcw, Clock, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  pending:  { label: "Pending",  cls: "bg-amber-100  text-amber-700  border-amber-200"  },
  reviewed: { label: "Reviewed", cls: "bg-blue-100   text-blue-700   border-blue-200"   },
  resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

const FILTER_OPTS = [
  { value: "",         label: "All Requests" },
  { value: "pending",  label: "Pending"      },
  { value: "reviewed", label: "Reviewed"     },
  { value: "resolved", label: "Resolved"     },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>
      {m.label}
    </span>
  );
}

export default function RevisionInbox() {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("");
  const [busy, setBusy]         = useState(null);   // mid currently being updated

  const load = useCallback(() => {
    setLoading(true);
    const q = filter ? `?status=${filter}` : "";
    api.get(`/communications/revision-requests${q}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Could not load revision requests"))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markStatus = async (mid, newStatus) => {
    setBusy(mid);
    try {
      const res = await api.patch(`/communications/revision-requests/${mid}/status`, { status: newStatus });
      if (res.data.idempotent) {
        toast.info(`Already marked ${newStatus}`);
      } else {
        toast.success(`Marked ${newStatus}`);
      }
      setItems((prev) =>
        prev.map((it) => it.id === mid ? { ...it, revision_request_status: newStatus } : it)
      );
    } catch {
      toast.error("Could not update status");
    } finally {
      setBusy(null);
    }
  };

  const filteredItems = filter
    ? items.filter((it) => (it.revision_request_status || "pending") === filter)
    : items;

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-5" data-testid="revision-inbox">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Communications</p>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <RotateCcw className="h-7 w-7 text-amber-500" />
            Revision Requests
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Client requests for revised estimates submitted through the secure portal.
          </p>
        </div>

        {/* Status filter */}
        <div className="flex items-center gap-2" data-testid="revision-filter">
          {FILTER_OPTS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              data-testid={`revision-filter-${opt.value || "all"}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === opt.value
                  ? "bg-slate-900 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400" data-testid="revision-loading">
          <Clock className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="revision-empty">
          <Inbox className="h-10 w-10 text-slate-300 mb-3" />
          <p className="text-slate-500 font-medium">No revision requests</p>
          <p className="text-slate-400 text-sm mt-1">
            {filter ? `No requests with status "${filter}"` : "No portal revision requests yet"}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-testid="revision-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50 text-left">
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Client</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Estimate</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Requested</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Message</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, idx) => {
                const status = item.revision_request_status || "pending";
                const isBusy = busy === item.id;
                return (
                  <tr
                    key={item.id}
                    data-testid={`revision-row-${item.id}`}
                    className={`border-b border-slate-50 hover:bg-slate-50/60 transition-colors ${idx % 2 === 0 ? "" : "bg-slate-50/30"}`}
                  >
                    {/* Client */}
                    <td className="px-4 py-3">
                      <span className="font-medium text-slate-800">{item.client_name}</span>
                    </td>

                    {/* Estimate */}
                    <td className="px-4 py-3 text-slate-600">
                      {item.estimate_number || item.related_estimate_id?.slice(0, 8) || "—"}
                    </td>

                    {/* Requested at */}
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {fmtDate(item.sent_at)}
                    </td>

                    {/* Message preview */}
                    <td className="px-4 py-3 text-slate-500 max-w-[280px]">
                      <span className="line-clamp-2" title={item.content}>
                        {item.content || "—"}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <StatusBadge status={status} />
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {/* Open Estimate */}
                        {item.related_estimate_id && (
                          <button
                            data-testid={`revision-open-estimate-${item.id}`}
                            onClick={() => navigate(`/sales/estimates/${item.related_estimate_id}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                            title="Open Estimate"
                          >
                            <FileText className="h-3.5 w-3.5" />
                            Estimate
                          </button>
                        )}

                        {/* Open Client */}
                        {item.client_id && (
                          <button
                            data-testid={`revision-open-client-${item.id}`}
                            onClick={() => navigate(`/client-workspace/${item.client_id}`)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
                            title="Open Client"
                          >
                            <User className="h-3.5 w-3.5" />
                            Client
                          </button>
                        )}

                        {/* Mark Reviewed */}
                        {status !== "reviewed" && status !== "resolved" && (
                          <button
                            data-testid={`revision-mark-reviewed-${item.id}`}
                            onClick={() => markStatus(item.id, "reviewed")}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            Reviewed
                          </button>
                        )}

                        {/* Mark Resolved */}
                        {status !== "resolved" && (
                          <button
                            data-testid={`revision-mark-resolved-${item.id}`}
                            onClick={() => markStatus(item.id, "resolved")}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-50 transition-colors"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Resolved
                          </button>
                        )}

                        {status === "resolved" && (
                          <span className="text-xs text-slate-400 italic">Done</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 text-xs text-slate-400">
            {filteredItems.length} request{filteredItems.length !== 1 ? "s" : ""}
            {filter ? ` (filtered by "${filter}")` : ""}
          </div>
        </div>
      )}
    </div>
  );
}
